/* eslint-disable react-hooks/exhaustive-deps */
import DocumentViewerModal from "components/DocumentViewer";
import { StatusChip } from "components/StatusChip";
import { useHasRole } from "contexts/AuthContext";
import { useState } from "react";
import { COMPLIANT_STATUS, FEEDBACK_TYPE } from "utils/constantValues";
import { ADVOCATE_ROLES } from "utils/roles";
import { useTranslation } from "contexts/i18nContext";
import AddDecisionModal from "./AddDecisionModal";
import AddFeedbackModal from "./AddFeedbackModal";
import UnderConstractionReaasignProsecutorModal from "./UnderConstractionReaasignProsecutorModal";

export default function DecisionCard({
  complaintData,
  investigationData,
  litigationData,
  allInvestigationData,
  onInvestigationRefresh,
  onComplaintsRefresh,
}: any) {
  const { t } = useTranslation();
  const canCreateInvestigationDecision = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canCreateInvestigationFeedback = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
  ]);

  const canCoordinatorConfirmSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
  ]);

  const [isOpen, setIsOpen] = useState(false);
  const [expandedDecisions, setExpandedDecisions] = useState<string[]>([]);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [coordinatorConfirmationModal, setCoordinatorConfirmationModal] =
    useState<boolean>(false);

  const toggleDecisionExpansion = (id: string) => {
    setExpandedDecisions((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  // Sort investigations by date (newest first)
  const sortedInvestigations = allInvestigationData?.sort(
    (a: any, b: any) =>
      new Date(b?.created_at ?? "").getTime() -
      new Date(a?.created_at ?? "").getTime(),
  );
  const latestInvestigation = sortedInvestigations?.[0];

  return (
    <div className="mb-8">
      {/* Header Section */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-red-50 flex items-center justify-center border border-red-100">
                <svg
                  className="w-5 h-5 text-red-800"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t("prosecutorDecisions")}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t("case")} #{complaintData?.complaint_number}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-red-900 text-red-100 text-xs px-3 py-1 rounded-full">
                {sortedInvestigations?.length || 0} {t("decisions")}
              </div>
            </div>
          </div>
          {/* thin accent line */}
          <div className="mt-4 h-0.5 bg-red-800 rounded-full" />
        </div>

        {/* Assigned Prosecutor Section */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-12 h-12 rounded-full bg-gray-800 border-2 border-white flex items-center justify-center">
                  <span className="text-lg font-bold text-yellow-500">
                    {complaintData?.assigned_prosecutor
                      ?.split(" ")
                      .map((n: string) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2) || "PP"}
                  </span>
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                  <span className="text-xs text-white">✓</span>
                </div>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {t("assignedProsecutor")}
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  {complaintData?.assigned_prosecutor || t("notAssignedYet")}
                </p>
              </div>
            </div>

            {canCoordinatorConfirmSummon &&
              complaintData.status === COMPLIANT_STATUS.UNDER_INVESTIGATION && (
                <button
                  className="flex items-center gap-2 px-4 py-2.5 bg-red-800 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                  onClick={() => setCoordinatorConfirmationModal(true)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    />
                  </svg>
                  {t("reassignProsecutor")}
                </button>
              )}
          </div>
        </div>

        {/* Decisions Section */}
        <div className="p-6">
          <div className="space-y-4">
            {/* Current/Latest Decision */}
            {latestInvestigation && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() =>
                    toggleDecisionExpansion(latestInvestigation.uu_id)
                  }
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-red-800"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">
                        {t("latestDecision")}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs font-medium text-red-800 bg-red-50 px-2 py-0.5 rounded">
                          {latestInvestigation?.prosecutor_decision_type ||
                            t("decision")}
                        </span>
                        <span className="text-xs text-gray-500">
                          {latestInvestigation?.investigation_start_date}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button className="text-gray-500 hover:text-gray-700">
                    <svg
                      className={`w-5 h-5 transition-transform ${expandedDecisions.includes(latestInvestigation.uu_id) ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>

                {/* Decision Details - Expandable */}
                {expandedDecisions.includes(latestInvestigation.uu_id) && (
                  <div className="px-4 pb-4 pt-2 border-t border-gray-200 animate-fadeIn">
                    {/* Decision Summary */}
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <h5 className="text-sm font-semibold text-gray-700">
                          {t("decisionSummary")}
                        </h5>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {latestInvestigation?.prosecutor_decision_summary ||
                            t("noSummaryProvided")}
                        </p>
                      </div>
                    </div>

                    {/* Decision Documents */}
                    {latestInvestigation?.documents?.filter(
                      (a: any) =>
                        a?.document_type === "prosecutor_decision_document",
                    ).length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <svg
                            className="w-4 h-4 text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <h5 className="text-sm font-semibold text-gray-700">
                            {t("supportingDocuments")}
                          </h5>
                        </div>
                        <DocumentViewerModal
                          documents={latestInvestigation?.documents?.filter(
                            (a: any) =>
                              a?.document_type ===
                              "prosecutor_decision_document",
                          )}
                          baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                        />
                      </div>
                    )}

                    {latestInvestigation?.documents?.filter(
                      (a: any) => a?.document_type === "charge_document",
                    ).length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <svg
                            className="w-4 h-4 text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <h5 className="text-sm font-semibold text-gray-700">
                            {t("chargeDocuments")}
                          </h5>
                        </div>
                        <DocumentViewerModal
                          documents={latestInvestigation?.documents?.filter(
                            (a: any) => a?.document_type === "charge_document",
                          )}
                          baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                        />
                      </div>
                    )}

                    {/* Feedback Section */}
                    {latestInvestigation?.director_decision_feedbacks &&
                      latestInvestigation?.director_decision_feedbacks?.length >
                        0 && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <svg
                                className="w-4 h-4 text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                                />
                              </svg>
                              <h5 className="text-sm font-semibold text-gray-700">
                                {t("directorFeedback")}
                              </h5>
                            </div>
                            <StatusChip
                              status={latestInvestigation?.feedback_status}
                            />
                          </div>
                          <div className="bg-white p-3 rounded border border-gray-200">
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {latestInvestigation
                                ?.director_decision_feedbacks[0]?.comment || ""}
                            </p>
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}

            {/* Previous Decisions - Collapsible */}
            {sortedInvestigations?.length > 1 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {t("previousDecisions")} (
                      {sortedInvestigations.length - 1})
                    </h4>
                    <button
                      onClick={() =>
                        setExpandedDecisions((prev) =>
                          prev.length === sortedInvestigations.length - 1
                            ? []
                            : sortedInvestigations
                                .slice(1)
                                .map((item: any) => item.uu_id),
                        )
                      }
                      className="text-xs font-medium text-red-800 hover:text-red-700"
                    >
                      {expandedDecisions.length ===
                      sortedInvestigations.length - 1
                        ? t("collapseAll")
                        : t("expandAll")}
                    </button>
                  </div>
                </div>

                <div className="divide-y divide-gray-200">
                  {sortedInvestigations
                    ?.slice(1)
                    .map((obj: any, index: number) => {
                      const filtered =
                        obj?.documents &&
                        obj?.documents?.filter(
                          (a: any) => a?.document_type === "charge_document",
                        );
                      return (
                        <div key={obj.uu_id}>
                          <div
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => toggleDecisionExpansion(obj.uu_id)}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                                <svg
                                  className="w-4 h-4 text-gray-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {t("decision")} #{index + 2}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-xs text-gray-600">
                                    {obj?.prosecutor_decision_type}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    •
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {obj?.investigation_start_date}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <button className="text-gray-500 hover:text-gray-700">
                              <svg
                                className={`w-5 h-5 transition-transform ${expandedDecisions.includes(obj.uu_id) ? "rotate-180" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                          </div>

                          {expandedDecisions.includes(obj.uu_id) && (
                            <div className="px-4 pb-4 pt-2 border-t border-gray-200 animate-fadeIn">
                              {/* Previous Decision Summary */}
                              <div className="mb-3">
                                <h6 className="text-xs font-medium text-gray-700 mb-2">
                                  {t("decisionSummary")}
                                </h6>
                                <div className="bg-white p-3 rounded border border-gray-200">
                                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                                    {obj?.prosecutor_decision_summary ||
                                      t("noSummaryProvided")}
                                  </p>
                                </div>
                              </div>

                              {/* Decision Documents */}
                              {filtered && filtered.length > 0 && (
                                <div className="mb-4">
                                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                                    {t("decisionDocuments")}
                                  </p>
                                  <DocumentViewerModal
                                    documents={filtered as any}
                                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                                  />
                                </div>
                              )}

                              {/* Previous Feedback if exists */}
                              {obj?.director_decision_feedbacks &&
                                obj?.director_decision_feedbacks?.length >
                                  0 && (
                                  <div className="bg-gray-50 p-3 rounded border border-gray-200">
                                    <div className="flex items-center gap-2 mb-2">
                                      <StatusChip
                                        status={obj?.feedback_status}
                                      />
                                      <span className="text-xs text-gray-600">
                                        {t("feedback")}
                                      </span>
                                    </div>
                                    <p className="text-xs text-gray-700">
                                      {obj?.director_decision_feedbacks[0]
                                        ?.comment || ""}
                                    </p>
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* No Decisions State */}
            {!sortedInvestigations?.length && (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <p className="text-gray-600 font-medium">
                  {t("noDecisionsYet")}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {t("noProsecutorDecisionsMade")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        {/* Add Decision Button */}
        {canCreateInvestigationDecision &&
          (!investigationData ||
            (investigationData &&
              investigationData?.feedback_status === FEEDBACK_TYPE?.REVISE)) &&
          !investigationData?.is_backto_prosecutor && (
            <button
              className="flex items-center gap-2 px-6 py-3 bg-red-800 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
              onClick={() => setIsOpen(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t("addProsecutorDecision")}
            </button>
          )}

        {/* Add Feedback Button */}
        {canCreateInvestigationFeedback &&
          investigationData &&
          investigationData.prosecutor_decision_type &&
          investigationData?.feedback_status !== FEEDBACK_TYPE?.APPROVED && (
            <button
              className="flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
              onClick={() => setIsFeedbackOpen(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                />
              </svg>
              {t("addDirectorFeedback")}
            </button>
          )}
      </div>

      {/* Modals */}
      <AddDecisionModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        complaintData={complaintData}
        onRefresh={onInvestigationRefresh}
      />

      {complaintData && (
        <UnderConstractionReaasignProsecutorModal
          isOpen={coordinatorConfirmationModal}
          setIsOpen={setCoordinatorConfirmationModal}
          complaintData={complaintData}
          onComplaintsRefresh={onComplaintsRefresh}
        />
      )}

      {investigationData && (
        <AddFeedbackModal
          isOpen={isFeedbackOpen}
          setIsOpen={setIsFeedbackOpen}
          complaintData={complaintData}
          investigationData={investigationData}
          onRefresh={onInvestigationRefresh}
        />
      )}
    </div>
  );
}
